import { type APIEntity, APIEntityQuery } from '@shared/api/types'
import { type EntityReference, UserRole } from '@shared/types'
import { stringToArray } from '@shared/utils'
import { type Session } from 'next-auth'

export const DEFAULT_ITEMS_PER_REQUEST = 20

type EntityAPIQueryParams = {
	name?: string | string[]
	ids?: (string | number)[]
	site?: number | string | (number | string)[]
	operationNames?: string[]
	operationName?: string
	shouldGetAll?: boolean
	exclude?: number[]
	accessFilter?: string
	includeInactive?: boolean
	includeDirectors?: boolean
	page?: number
}

export function getEntityGETEndpoint(entity: APIEntity, params: EntityAPIQueryParams = {}) {
	const { name, ids, site, shouldGetAll, exclude, operationNames, operationName, accessFilter, includeInactive, includeDirectors, page } = params
	const searchParams = new URLSearchParams()

	const parsedNames = !Array.isArray(name) ? name : `[${name.toString()}]`
	if (name) searchParams.append(APIEntityQuery.name, parsedNames || '')
	if (ids) searchParams.append(APIEntityQuery.ids, `[${ids.toString()}]`)
	if (site) {
		const siteParam = Array.isArray(site) ? `[${site.toString()}]` : site.toString()
		searchParams.append(APIEntityQuery.site, siteParam)
	}
	const opNames = operationNames ?? (operationName ? [operationName] : undefined)
	if (opNames?.length) searchParams.append(APIEntityQuery.operationName, `[${opNames.join(',')}]`)
	if (shouldGetAll) searchParams.append(APIEntityQuery.shouldGetAll, String(shouldGetAll))
	if (exclude) searchParams.append(APIEntityQuery.exclude, `[${exclude.toString()}]`)
	if (accessFilter) searchParams.append(APIEntityQuery.accessFilter, accessFilter)
	if (includeInactive) searchParams.append(APIEntityQuery.includeInactive, String(includeInactive))
	if (includeDirectors) searchParams.append(APIEntityQuery.includeDirectors, String(includeDirectors))
	if (page && page > 1) searchParams.append(APIEntityQuery.page, page.toString())

	const stringParams = searchParams.size > 0 ? `?${searchParams.toString()}` : ''

	return `/api/${entity}${stringParams}`
}

export function getQueryParams(requestUrl: string) {
	const { searchParams } = new URL(requestUrl)

	const nameParamValue = searchParams.get(APIEntityQuery.name)
	const idsParamValue = searchParams.get(APIEntityQuery.ids)
	const siteParamValue = searchParams.get(APIEntityQuery.site)
	const isActiveParamValue = searchParams.get(APIEntityQuery.isActive)
	const includeInactive = searchParams.get(APIEntityQuery.includeInactive)
	const includeDirectors = searchParams.get(APIEntityQuery.includeDirectors)
	const shouldGetAll = searchParams.get(APIEntityQuery.shouldGetAll)
	const excludeParamValue = searchParams.get(APIEntityQuery.exclude)
	const operationNameParamValue = searchParams.get(APIEntityQuery.operationName)
	const accessFilter = searchParams.get(APIEntityQuery.accessFilter)
	const pageParamValue = Number(searchParams.get(APIEntityQuery.page))
	const isOperationNamesArray = operationNameParamValue?.startsWith('[') && operationNameParamValue?.endsWith(']')
	const operationNames = operationNameParamValue
		? isOperationNamesArray
			? stringToArray(operationNameParamValue).filter(Boolean)
			: [operationNameParamValue]
		: null

	let isActive
	if (isActiveParamValue === null) isActive = undefined
	else isActive = isActiveParamValue !== 'false'

	const ids = idsParamValue
		? stringToArray(idsParamValue)
				.map(Number)
				.filter(id => !isNaN(id) && id !== 0)
		: undefined

	const exclude = excludeParamValue
		? stringToArray(excludeParamValue)
				.map(Number)
				.filter(id => !isNaN(id))
		: undefined

	const isArrayOfNames = nameParamValue && nameParamValue.at(0) === '[' && nameParamValue.at(-1) === ']'
	const isArrayOfSites = siteParamValue && siteParamValue.at(0) === '[' && siteParamValue.at(-1) === ']'

	const site =
		siteParamValue && isArrayOfSites
			? stringToArray(siteParamValue)
					.map(s => {
						const num = Number(s)
						return !isNaN(num) ? num : s
					})
					.filter(s => s !== '' && s !== 0)
			: siteParamValue

	return {
		name: !isArrayOfNames ? nameParamValue : null,
		names: isArrayOfNames ? stringToArray(nameParamValue).filter(Boolean) : null,
		site,
		operationNames,
		isActive,
		includeInactive: Boolean(includeInactive),
		includeDirectors: Boolean(includeDirectors),
		ids,
		shouldGetAll: Boolean(shouldGetAll),
		exclude,
		accessFilter,
		page: Number.isInteger(pageParamValue) && pageParamValue > 1 ? pageParamValue : 1,
	}
}

export const formatSession = (session: Session | null) => {
	const role = session?.user.role ?? null
	const name = session?.user.name ?? ''
	const id = session?.user.id
	const image = session?.user.image ?? null
	const lastLoginAt = session?.user.lastLoginAt ?? null

	const isAdmin = role === UserRole.ADMIN
	const isGuest = role === UserRole.GUEST
	const isEngineer = role === UserRole.USER
	const isSiteManager = role === UserRole.SITE_MANAGER
	const isDirector = role === UserRole.DIRECTOR
	const site =session?.user.site as EntityReference & { abbreviation: string | null }
	const jmtAccess = Boolean(session?.user.jmtAccess)
	const qmtAccess = Boolean(session?.user.qmtAccess)
	const hasJmtAccess = jmtAccess
	const hasQmtAccess = qmtAccess

	return {
		isAdmin,
		isGuest,
		role,
		isEngineer,
		isSiteManager,
		isDirector,
		hasJmtAccess,
		hasQmtAccess,
		name,
		id,
		avatarUrl: image,
		lastLoginAt: lastLoginAt ? new Date(lastLoginAt) : null,
		userSite: site,
	}
}
